import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../config/supabase';
import './BundleReviewPage.css';

/**
 * BundleReviewPage - Admin tool for reviewing bundles, their children,
 * and BOQ item assignments. Shows actual BOQ entries (old boq_description
 * and new boq_items/master_boq_items) and allows moving/deleting them.
 * Access via /admin/bundle-review (no sidebar link needed).
 */
const BundleReviewPage = () => {
  // Data state
  const [masterItems, setMasterItems] = useState([]);
  const [boqCategories, setBoqCategories] = useState({});
  const [costTypes, setCostTypes] = useState({});
  const [masterBoqItems, setMasterBoqItems] = useState({});
  const [bbChapters, setBbChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // UI state
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedBundles, setExpandedBundles] = useState(new Set());
  const [moveModal, setMoveModal] = useState(null); // { entryIndex, sourceItemId, bundleId, entry }

  // ─── Data fetching ───────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, catRes, costRes, mboqRes, chaptersRes] = await Promise.all([
        supabase
          .from('master_items')
          .select(
            'id, item_name, is_bundle, bundle_type, bundle_items, include_in_boq, boq_description, boq_items, project_phase_id, bb_chapter_id',
          )
          .order('item_name'),
        supabase.from('reference_boq_categories').select('id, name, category_number'),
        supabase.from('reference_cost_types').select('id, name'),
        supabase
          .from('master_boq_items')
          .select('id, name, bb_chapter_id, cost_type_id, estimated_cost'),
        supabase
          .from('reference_bb_chapters')
          .select('id, code, name_he, friendly_name, depth, parent_id')
          .in('depth', [0, 1])
          .order('code'),
      ]);

      if (itemsRes.error) throw itemsRes.error;
      if (catRes.error) throw catRes.error;
      if (costRes.error) throw costRes.error;
      if (mboqRes.error) throw mboqRes.error;
      if (chaptersRes.error) throw chaptersRes.error;

      setMasterItems(itemsRes.data || []);

      // Index categories by id
      const catMap = {};
      (catRes.data || []).forEach(c => {
        catMap[c.id] = `${c.category_number} - ${c.name}`;
      });
      setBoqCategories(catMap);

      // Index cost types by id
      const ctMap = {};
      (costRes.data || []).forEach(ct => {
        ctMap[ct.id] = ct.name;
      });
      setCostTypes(ctMap);

      // Index master_boq_items by id
      const mboqMap = {};
      (mboqRes.data || []).forEach(m => {
        mboqMap[m.id] = m;
      });
      setMasterBoqItems(mboqMap);

      setBbChapters(chaptersRes.data || []);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Computed: build bundle structures ─────────────────────────

  // Index all items by id for quick lookup
  const itemsById = useMemo(() => {
    const map = {};
    masterItems.forEach(item => {
      map[item.id] = item;
    });
    return map;
  }, [masterItems]);

  // Build bundle data: each bundle with its children resolved
  const bundles = useMemo(() => {
    return masterItems
      .filter(item => item.is_bundle)
      .map(bundle => {
        const childIds = (bundle.bundle_items?.items || []).map(c => c.item_id);
        const children = childIds.map(id => itemsById[id]).filter(Boolean);

        // Check for BOQ conflicts: parent has BOQ entries AND at least one child also has BOQ entries
        const parentHasBoq = hasBoqEntries(bundle);
        const childrenWithBoq = children.filter(c => hasBoqEntries(c));
        const hasConflict = parentHasBoq && childrenWithBoq.length > 0;

        return {
          ...bundle,
          children,
          parentHasBoq,
          childrenWithBoq,
          hasConflict,
        };
      })
      .sort((a, b) => a.item_name.localeCompare(b.item_name, 'he'));
  }, [masterItems, itemsById]);

  // ─── Stats ─────────────────────────────────────────────────────

  const stats = useMemo(() => {
    const total = bundles.length;
    const withBoq = bundles.filter(b => b.parentHasBoq).length;
    const conflicts = bundles.filter(b => b.hasConflict).length;
    const choiceBundles = bundles.filter(b => b.bundle_type === 'choice').length;
    const simpleBundles = total - choiceBundles;
    return { total, withBoq, conflicts, choiceBundles, simpleBundles };
  }, [bundles]);

  // ─── Filtering ─────────────────────────────────────────────────

  const filteredBundles = useMemo(() => {
    let items = bundles;

    switch (filter) {
      case 'conflicts':
        items = items.filter(b => b.hasConflict);
        break;
      case 'simple':
        items = items.filter(b => b.bundle_type !== 'choice');
        break;
      case 'choice':
        items = items.filter(b => b.bundle_type === 'choice');
        break;
      case 'with-boq':
        items = items.filter(b => b.parentHasBoq || b.childrenWithBoq.length > 0);
        break;
      default:
        break;
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(
        b =>
          b.item_name.toLowerCase().includes(term) ||
          b.children.some(c => c.item_name.toLowerCase().includes(term)),
      );
    }

    return items;
  }, [bundles, filter, searchTerm]);

  // ─── Helpers ───────────────────────────────────────────────────

  // Check if an item has any BOQ entries (old or new system)
  function hasBoqEntries(item) {
    if (!item) return false;
    const oldEntries = item.boq_description?.entries || [];
    const newEntries = item.boq_items || [];
    return oldEntries.length > 0 || newEntries.length > 0;
  }

  // Get BOQ entries for an item, tagged with system type
  function getBoqEntries(item) {
    if (!item) return [];
    const entries = [];

    // Old system: boq_description.entries[]
    const oldEntries = item.boq_description?.entries || [];
    oldEntries.forEach((entry, idx) => {
      entries.push({
        system: 'old',
        index: idx,
        categoryId: entry.category_id,
        categoryName: boqCategories[entry.category_id] || entry.category_id || '-',
        description: entry.description || '',
        measurementType: costTypes[entry.measurement_type] || '',
        quantityType: entry.quantity?.type || '',
        raw: entry,
      });
    });

    // New system: boq_items[] → master_boq_items
    const newEntries = item.boq_items || [];
    newEntries.forEach((mboqId, idx) => {
      const mboq = masterBoqItems[mboqId];
      entries.push({
        system: 'new',
        index: idx,
        masterBoqItemId: mboqId,
        name: mboq?.name || mboqId,
        estimatedCost: mboq?.estimated_cost,
        costType: mboq?.cost_type_id ? costTypes[mboq.cost_type_id] : '',
        raw: mboq,
      });
    });

    return entries;
  }

  // ─── Actions ───────────────────────────────────────────────────

  // Toggle include_in_boq for an item
  const handleToggleBoq = async (itemId, currentValue) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('master_items')
        .update({ include_in_boq: !currentValue })
        .eq('id', itemId);
      if (error) throw error;

      // Update local state
      setMasterItems(prev =>
        prev.map(item => (item.id === itemId ? { ...item, include_in_boq: !currentValue } : item)),
      );
    } catch (err) {
      console.error('Error toggling BOQ:', err);
      alert('שגיאה בעדכון: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete a BOQ entry from an item's boq_description
  const handleDeleteOldEntry = async (itemId, entryIndex) => {
    if (!window.confirm('למחוק את שורת ה-BOQ הזו?')) return;

    const item = itemsById[itemId];
    if (!item?.boq_description?.entries) return;

    setSaving(true);
    try {
      const newEntries = [...item.boq_description.entries];
      newEntries.splice(entryIndex, 1);
      const newBoqDescription = { ...item.boq_description, entries: newEntries };

      const { error } = await supabase
        .from('master_items')
        .update({ boq_description: newBoqDescription })
        .eq('id', itemId);
      if (error) throw error;

      setMasterItems(prev =>
        prev.map(i => (i.id === itemId ? { ...i, boq_description: newBoqDescription } : i)),
      );
    } catch (err) {
      console.error('Error deleting BOQ entry:', err);
      alert('שגיאה במחיקה: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Delete a BOQ entry from an item's boq_items[] (new system)
  const handleDeleteNewEntry = async (itemId, entryIndex) => {
    if (!window.confirm('למחוק את שורת ה-BOQ הזו?')) return;

    const item = itemsById[itemId];
    if (!item?.boq_items) return;

    setSaving(true);
    try {
      const newBoqItems = [...item.boq_items];
      newBoqItems.splice(entryIndex, 1);

      const { error } = await supabase
        .from('master_items')
        .update({ boq_items: newBoqItems.length > 0 ? newBoqItems : null })
        .eq('id', itemId);
      if (error) throw error;

      setMasterItems(prev =>
        prev.map(i =>
          i.id === itemId ? { ...i, boq_items: newBoqItems.length > 0 ? newBoqItems : null } : i,
        ),
      );
    } catch (err) {
      console.error('Error deleting BOQ entry:', err);
      alert('שגיאה במחיקה: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Open move modal: pick a target item within the same bundle
  const handleOpenMoveModal = (entry, sourceItemId, bundleId) => {
    setMoveModal({ entry, sourceItemId, bundleId });
  };

  // Execute move: remove entry from source, add to target
  const handleMoveEntry = async targetItemId => {
    if (!moveModal) return;
    const { entry, sourceItemId } = moveModal;

    if (targetItemId === sourceItemId) {
      setMoveModal(null);
      return;
    }

    setSaving(true);
    try {
      if (entry.system === 'old') {
        // Remove from source's boq_description
        const sourceItem = itemsById[sourceItemId];
        const sourceEntries = [...(sourceItem.boq_description?.entries || [])];
        sourceEntries.splice(entry.index, 1);
        const newSourceBoqDesc = { ...sourceItem.boq_description, entries: sourceEntries };

        // Add to target's boq_description
        const targetItem = itemsById[targetItemId];
        const targetEntries = [...(targetItem.boq_description?.entries || [])];
        targetEntries.push(entry.raw);
        const newTargetBoqDesc = {
          ...(targetItem.boq_description || {}),
          entries: targetEntries,
        };

        // Update both in a single batch
        const { error: sourceErr } = await supabase
          .from('master_items')
          .update({ boq_description: newSourceBoqDesc })
          .eq('id', sourceItemId);
        if (sourceErr) throw sourceErr;

        const { error: targetErr } = await supabase
          .from('master_items')
          .update({ boq_description: newTargetBoqDesc })
          .eq('id', targetItemId);
        if (targetErr) throw targetErr;

        // Update local state
        setMasterItems(prev =>
          prev.map(i => {
            if (i.id === sourceItemId) return { ...i, boq_description: newSourceBoqDesc };
            if (i.id === targetItemId) return { ...i, boq_description: newTargetBoqDesc };
            return i;
          }),
        );
      } else if (entry.system === 'new') {
        // Remove from source's boq_items[]
        const sourceItem = itemsById[sourceItemId];
        const sourceBoqItems = [...(sourceItem.boq_items || [])];
        sourceBoqItems.splice(entry.index, 1);

        // Add to target's boq_items[]
        const targetItem = itemsById[targetItemId];
        const targetBoqItems = [...(targetItem.boq_items || []), entry.masterBoqItemId];

        const { error: sourceErr } = await supabase
          .from('master_items')
          .update({ boq_items: sourceBoqItems.length > 0 ? sourceBoqItems : null })
          .eq('id', sourceItemId);
        if (sourceErr) throw sourceErr;

        const { error: targetErr } = await supabase
          .from('master_items')
          .update({ boq_items: targetBoqItems })
          .eq('id', targetItemId);
        if (targetErr) throw targetErr;

        setMasterItems(prev =>
          prev.map(i => {
            if (i.id === sourceItemId)
              return { ...i, boq_items: sourceBoqItems.length > 0 ? sourceBoqItems : null };
            if (i.id === targetItemId) return { ...i, boq_items: targetBoqItems };
            return i;
          }),
        );
      }

      setMoveModal(null);
    } catch (err) {
      console.error('Error moving BOQ entry:', err);
      alert('שגיאה בהעברה: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Build hierarchical chapter options: depth-0 chapters, then indented depth-1 sections
  const chapterOptions = useMemo(() => {
    const depth0 = bbChapters.filter(c => c.depth === 0);
    const result = [];
    depth0.forEach(ch => {
      result.push(ch);
      const sections = bbChapters
        .filter(s => s.depth === 1 && s.parent_id === ch.id)
        .sort((a, b) => a.code.localeCompare(b.code));
      sections.forEach(s => result.push(s));
    });
    return result;
  }, [bbChapters]);

  // Update bb_chapter_id on a master_item
  const handleBbChapterChange = async (itemId, chapterId) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('master_items')
        .update({ bb_chapter_id: chapterId || null })
        .eq('id', itemId);
      if (error) throw error;

      setMasterItems(prev =>
        prev.map(item =>
          item.id === itemId ? { ...item, bb_chapter_id: chapterId || null } : item,
        ),
      );
    } catch (err) {
      console.error('Error updating BB chapter:', err);
      alert('שגיאה בעדכון פרק: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle expand/collapse a bundle
  const toggleExpand = bundleId => {
    setExpandedBundles(prev => {
      const next = new Set(prev);
      if (next.has(bundleId)) {
        next.delete(bundleId);
      } else {
        next.add(bundleId);
      }
      return next;
    });
  };

  // Expand all / collapse all
  const expandAll = () => {
    setExpandedBundles(new Set(filteredBundles.map(b => b.id)));
  };

  const collapseAll = () => {
    setExpandedBundles(new Set());
  };

  // ─── Render: BOQ entries for a single item ─────────────────────

  const renderBoqEntries = (item, bundleId) => {
    const entries = getBoqEntries(item);

    if (entries.length === 0) {
      return <div className="no-boq-entries">אין שורות BOQ</div>;
    }

    return (
      <div className="boq-entries">
        {entries.map((entry, idx) => (
          <div key={idx} className={`boq-entry ${entry.system === 'new' ? 'new-system' : ''}`}>
            <span
              className={`boq-entry-system-tag ${entry.system === 'old' ? 'tag-old' : 'tag-new'}`}
            >
              {entry.system === 'old' ? 'ישן' : 'חדש'}
            </span>
            <div className="boq-entry-content">
              {entry.system === 'old' ? (
                <>
                  <div className="boq-entry-category">{entry.categoryName}</div>
                  <div className="boq-entry-description">{entry.description || '(ריק)'}</div>
                  {(entry.measurementType || entry.quantityType) && (
                    <div className="boq-entry-measurement">
                      {entry.measurementType && `יחידה: ${entry.measurementType}`}
                      {entry.quantityType && ` | כמות: ${entry.quantityType}`}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="boq-entry-description">{entry.name}</div>
                  {entry.estimatedCost && (
                    <div className="boq-entry-measurement">
                      עלות משוערת: ₪{Number(entry.estimatedCost).toLocaleString()}
                      {entry.costType && ` | ${entry.costType}`}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="boq-entry-actions">
              <button
                className="br-action-btn move"
                onClick={e => {
                  e.stopPropagation();
                  handleOpenMoveModal(entry, item.id, bundleId);
                }}
                title="העבר לפריט אחר בחבילה"
                disabled={saving}
              >
                העבר
              </button>
              <button
                className="br-action-btn delete"
                onClick={e => {
                  e.stopPropagation();
                  if (entry.system === 'old') {
                    handleDeleteOldEntry(item.id, entry.index);
                  } else {
                    handleDeleteNewEntry(item.id, entry.index);
                  }
                }}
                title="מחק שורת BOQ"
                disabled={saving}
              >
                מחק
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ─── Render: single item row (parent or child) ────────────────

  const renderItemRow = (item, role, bundleId) => {
    const isParent = role === 'parent';
    const entries = getBoqEntries(item);

    return (
      <div key={item.id} className={`bundle-item-row ${isParent ? 'is-parent' : 'is-child'}`}>
        <span className={`item-role-label ${isParent ? 'role-parent' : 'role-child'}`}>
          {isParent ? 'הורה' : 'ילד'}
        </span>
        <div className="item-info">
          <div className="item-name">
            {item.item_name}
            {entries.length > 0 && (
              <span style={{ color: '#6b7280', fontWeight: 400, fontSize: 12, marginRight: 6 }}>
                ({entries.length} שורות BOQ)
              </span>
            )}
          </div>
          <div className="item-controls-row">
            <div className="item-boq-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={item.include_in_boq || false}
                  onChange={() => handleToggleBoq(item.id, item.include_in_boq)}
                  disabled={saving}
                />
                הצג בכתב כמויות
              </label>
            </div>
            <div className="item-bb-chapter">
              <label>פרק BB: </label>
              <select
                value={item.bb_chapter_id || ''}
                onChange={e => handleBbChapterChange(item.id, e.target.value)}
                disabled={saving}
                style={{ fontSize: 12, padding: '2px 4px', maxWidth: 220 }}
              >
                <option value="">— לא ממופה —</option>
                {chapterOptions.map(ch => (
                  <option key={ch.id} value={ch.id}>
                    {ch.depth === 1 ? '  ↳ ' : ''}
                    {ch.code} - {ch.friendly_name || ch.name_he}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {renderBoqEntries(item, bundleId)}
        </div>
      </div>
    );
  };

  // ─── Render: bundle card ───────────────────────────────────────

  const renderBundleCard = bundle => {
    const isExpanded = expandedBundles.has(bundle.id);

    return (
      <div key={bundle.id} className={`bundle-card ${bundle.hasConflict ? 'has-conflict' : ''}`}>
        {/* Header */}
        <div className="bundle-card-header" onClick={() => toggleExpand(bundle.id)}>
          <span className={`bundle-expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
          <span className="bundle-card-title">{bundle.item_name}</span>
          <div className="bundle-card-badges">
            <span className="badge badge-type">
              {bundle.bundle_type === 'choice' ? 'בחירה' : 'פשוט'}
            </span>
            <span className={`badge ${bundle.parentHasBoq ? 'badge-boq' : 'badge-boq-off'}`}>
              {bundle.parentHasBoq ? 'BOQ הורה ✓' : 'BOQ הורה ✗'}
            </span>
            {bundle.childrenWithBoq.length > 0 && (
              <span className="badge badge-boq">BOQ ילדים: {bundle.childrenWithBoq.length}</span>
            )}
            {bundle.hasConflict && <span className="badge badge-conflict">⚠ כפילות אפשרית</span>}
            <span className="badge badge-children">{bundle.children.length} ילדים</span>
          </div>
        </div>

        {/* Expanded body */}
        {isExpanded && (
          <div className="bundle-card-body">
            {bundle.hasConflict && (
              <div className="conflict-warning">
                <span className="conflict-warning-icon">⚠️</span>
                גם להורה וגם לילדים יש שורות BOQ — ייתכנו כפילויות. בדוק והעבר/מחק לפי הצורך.
              </div>
            )}
            {/* Parent item */}
            {renderItemRow(bundle, 'parent', bundle.id)}
            {/* Children */}
            {bundle.children.map(child => renderItemRow(child, 'child', bundle.id))}
          </div>
        )}
      </div>
    );
  };

  // ─── Render: move modal ────────────────────────────────────────

  const renderMoveModal = () => {
    if (!moveModal) return null;

    const { sourceItemId, bundleId, entry } = moveModal;
    const bundle = bundles.find(b => b.id === bundleId);
    if (!bundle) return null;

    // Possible targets: parent + all children
    const targets = [bundle, ...bundle.children];
    const sourceItem = itemsById[sourceItemId];

    return (
      <div className="move-modal-overlay" onClick={() => setMoveModal(null)}>
        <div className="move-modal" onClick={e => e.stopPropagation()}>
          <h3>העבר שורת BOQ</h3>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
            מ: <strong>{sourceItem?.item_name}</strong>
            <br />
            שורה: {entry.system === 'old' ? entry.description?.substring(0, 60) : entry.name}
          </div>
          <div className="move-modal-targets">
            {targets.map(target => (
              <button
                key={target.id}
                className={`move-target-btn ${target.id === sourceItemId ? 'current' : ''}`}
                onClick={() => target.id !== sourceItemId && handleMoveEntry(target.id)}
                disabled={target.id === sourceItemId || saving}
              >
                {target.id === bundleId ? '🔷 ' : '  ↳ '}
                {target.item_name}
                {target.id === sourceItemId && ' (נוכחי)'}
              </button>
            ))}
          </div>
          <button className="move-modal-cancel" onClick={() => setMoveModal(null)}>
            ביטול
          </button>
        </div>
      </div>
    );
  };

  // ─── Main render ───────────────────────────────────────────────

  if (loading) {
    return (
      <div className="bundle-review-page">
        <div className="bundle-review-loading">טוען נתונים...</div>
      </div>
    );
  }

  return (
    <div className="bundle-review-page">
      {/* Header */}
      <div className="bundle-review-header">
        <h1>סקירת חבילות ו-BOQ</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {saving && <span className="saving-indicator">שומר...</span>}
          <input
            type="text"
            className="bundle-review-search"
            placeholder="חיפוש חבילה או פריט..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="bundle-review-stats">
        <div className="bundle-review-stat">
          סה&quot;כ חבילות: <strong>{stats.total}</strong>
        </div>
        <div className="bundle-review-stat">
          פשוטות: <strong>{stats.simpleBundles}</strong> | בחירה:{' '}
          <strong>{stats.choiceBundles}</strong>
        </div>
        <div className="bundle-review-stat">
          עם BOQ הורה: <strong>{stats.withBoq}</strong>
        </div>
        <div className={`bundle-review-stat ${stats.conflicts > 0 ? 'stat-warning' : ''}`}>
          כפילויות אפשריות: <strong>{stats.conflicts}</strong>
        </div>
      </div>

      {/* Filters */}
      <div className="bundle-review-filters">
        {[
          { key: 'all', label: `הכל (${stats.total})` },
          { key: 'conflicts', label: `⚠ כפילויות (${stats.conflicts})` },
          { key: 'with-boq', label: 'עם BOQ' },
          { key: 'simple', label: `פשוטות (${stats.simpleBundles})` },
          { key: 'choice', label: `בחירה (${stats.choiceBundles})` },
        ].map(f => (
          <button
            key={f.key}
            className={`bundle-filter-btn ${filter === f.key ? 'active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        <div style={{ marginRight: 'auto', display: 'flex', gap: 6 }}>
          <button className="bundle-filter-btn" onClick={expandAll}>
            פתח הכל
          </button>
          <button className="bundle-filter-btn" onClick={collapseAll}>
            סגור הכל
          </button>
        </div>
      </div>

      {/* Bundle cards */}
      {filteredBundles.length === 0 ? (
        <div className="bundle-review-empty">אין חבילות להצגה</div>
      ) : (
        filteredBundles.map(bundle => renderBundleCard(bundle))
      )}

      {/* Move modal */}
      {renderMoveModal()}
    </div>
  );
};

export default BundleReviewPage;
